import { type MeetingDetail, parseTranscriptLines } from '@fos/shared/contracts/meetings';
import { FileText } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { errorMessage, validationIssues } from '../../../lib/api';
import { plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Mono } from '../../../ui/Mono';
import { EmptyState } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE, TOUCH_TEXT } from '../../mobile';
import { TranscriptField } from './TranscriptField';

/** Linii widocznych przed „Pokaż całą transkrypcję”. */
export const TRANSCRIPT_PREVIEW_LINES = 12;

/**
 * Transkrypcja w widoku spotkania (Figma 05: panel po prawej, „Edytuj”): osoba, godzina (mono), wypowiedź.
 * Długa transkrypcja jest zwinięta do pierwszych linii. „Edytuj” (`onEdit`) otwiera `TranscriptDialog` u rodzica.
 */
export function TranscriptPanel({ meeting, onEdit }: { meeting: MeetingDetail; onEdit?: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const lines = parseTranscriptLines(meeting.transcriptText);
  const visible = expanded ? lines : lines.slice(0, TRANSCRIPT_PREVIEW_LINES);
  const hidden = lines.length - visible.length;

  return (
    <section id="transkrypcja" aria-labelledby="transkrypcja-tytul" className="scroll-mt-6">
      <div className="flex items-center justify-between gap-3">
        <h2 id="transkrypcja-tytul" className="text-body-s font-semibold text-fg">
          Transkrypcja
        </h2>
        {meeting.canEdit && onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className={`rounded-xs text-body-s font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
          >
            {lines.length > 0 ? 'Edytuj' : 'Dodaj'}
          </button>
        )}
      </div>

      {lines.length === 0 ? (
        <EmptyState
          compact
          icon={FileText}
          title="Brak transkrypcji"
          description={
            meeting.canEdit
              ? 'Wklej tekst albo wczytaj plik .txt, .vtt lub .srt. AI przygotuje z niego podsumowanie i zadania.'
              : 'Do tego spotkania nie dodano transkrypcji.'
          }
        />
      ) : (
        <>
          <ol aria-label="Wypowiedzi" className="mt-4 flex flex-col gap-4">
            {visible.map((line, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: linie transkrypcji nie mają identyfikatorów, kolejność stała
              <li key={index} className="min-w-0">
                {(line.speaker || line.time) && (
                  <p className="flex items-baseline gap-2">
                    {line.speaker && <span className="text-caption font-semibold text-fg">{line.speaker}</span>}
                    {line.time && <Mono className="text-micro text-fg-muted">{line.time}</Mono>}
                  </p>
                )}
                <p className="mt-0.5 text-caption break-words whitespace-pre-wrap text-fg-secondary">{line.text}</p>
              </li>
            ))}
          </ol>
          {(hidden > 0 || expanded) && lines.length > TRANSCRIPT_PREVIEW_LINES && (
            <Button
              variant="ghost"
              size="s"
              className="mt-3 -ml-2.5"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded
                ? 'Zwiń transkrypcję'
                : `Pokaż całą transkrypcję (jeszcze ${hidden} ${plural(hidden, ['linia', 'linie', 'linii'])})`}
            </Button>
          )}
        </>
      )}
    </section>
  );
}

/** Edycja samej transkrypcji (zapis tylko tego pola; wersja spotkania chroni przed nadpisaniem cudzych zmian). */
export function TranscriptDialog({
  open,
  onOpenChange,
  meeting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meeting: MeetingDetail;
}) {
  const utils = trpc.useUtils();
  const update = trpc.meetings.update.useMutation();
  const [value, setValue] = useState(meeting.transcriptText ?? '');
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: świeża treść przy każdym otwarciu
  useEffect(() => {
    if (!open) return;
    setValue(meeting.transcriptText ?? '');
    setError(null);
    setFormError(null);
  }, [open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setFormError(null);
    const next = value.trim() ? value : null;
    if (next === (meeting.transcriptText ?? null)) {
      onOpenChange(false);
      return;
    }
    try {
      await update.mutateAsync({ id: meeting.id, version: meeting.version, transcriptText: next });
      await utils.meetings.invalidate();
      onOpenChange(false);
      toast.success(next ? 'Zapisano transkrypcję' : 'Usunięto transkrypcję');
    } catch (err) {
      const issue = validationIssues(err).find((i) => i.path.startsWith('transcriptText'));
      if (issue) setError(issue.message);
      else setFormError(errorMessage(err, 'Nie udało się zapisać transkrypcji.'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title="Transkrypcja"
      className={FULLSCREEN_ON_PHONE}
      description="Instrukcje zapisane w transkrypcji AI traktuje jako treść rozmowy, nie jako polecenia."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="transcript-form" loading={update.isPending}>
            Zapisz
          </Button>
        </>
      }
    >
      <form id="transcript-form" onSubmit={submit} noValidate className="flex flex-col gap-3">
        {formError && <FormMessage>{formError}</FormMessage>}
        <TranscriptField value={value} onChange={setValue} error={error} rows={12} autoFocus />
      </form>
    </Dialog>
  );
}
